import React from 'react';
import { Link } from 'react-router-dom';
import {
  FaCode, FaBrain, FaShieldAlt, FaPlug, FaCogs, FaChartBar,
  FaArrowRight, FaCheckCircle, FaReact, FaMobileAlt
} from 'react-icons/fa';
import { motion } from 'motion/react';
import ScrollReveal from './ScrollReveal';
import PageNavbar from './PageNavbar';
import {
  defaultViewport,
  staggerContainer,
  staggerItem,
  getRevealProps,
} from '../motion/scrollMotion';
import Footer from './Footer';
import './DevelopmentPage.css';

const services = [
  {
    icon: <FaCode />,
    title: 'Custom Application Development',
    href: '/services/custom-application-development',
    desc: 'Bespoke web, mobile, and enterprise applications built with security-first architecture, modern technology stacks, and scalable design patterns.',
  },
  {
    icon: <FaBrain />,
    title: 'AI Development Solutions',
    href: '/services/ai-development-solutions',
    desc: 'Machine learning, NLP, computer vision, and intelligent automation solutions — designed with secure architecture and responsible AI practices.',
  },
  {
    icon: <FaShieldAlt />,
    title: 'Secure SDLC Services',
    href: '/services/secure-sdlc-services',
    desc: 'Integrate security into every phase of your development lifecycle — threat modeling, SAST/DAST/SCA tooling, code review, and CI/CD security gates.',
  },
  {
    icon: <FaPlug />,
    title: 'API Integration Services',
    href: '/services/api-integration-services',
    desc: 'Secure API design, development, and integration for connected systems — RESTful, GraphQL, webhooks, gateway management, and security hardening.',
  },
  {
    icon: <FaCogs />,
    title: 'Automation Tools',
    href: '/services/automation-tools',
    desc: 'Custom automation tools for security operations (SOAR), IT workflows, compliance reporting, and business processes — reducing manual effort and human error.',
  },
  {
    icon: <FaChartBar />,
    title: 'Security Dashboards',
    href: '/services/security-dashboards',
    desc: 'Custom security dashboards and reporting platforms — multi-source aggregation, real-time alerting, compliance mapping, and executive reporting.',
  },
];

const process = [
  { step: '01', name: 'Discovery', desc: 'Understand business goals, threat model, and technical constraints' },
  { step: '02', name: 'Design', desc: 'Secure architecture, technology selection, and scalable design patterns' },
  { step: '03', name: 'Develop', desc: 'Secure coding standards with SAST/DAST integrated into the workflow' },
  { step: '04', name: 'Test', desc: 'Functional, performance, and security testing including penetration testing' },
  { step: '05', name: 'Deploy', desc: 'Hardened production deployment with CI/CD security gates' },
  { step: '06', name: 'Support', desc: 'Ongoing maintenance, monitoring, and security patching' },
];

const techStacks = [
  { label: 'Frontend', items: ['React', 'Next.js', 'TypeScript', 'Tailwind'] },
  { label: 'Backend', items: ['Node.js', 'Python', '.NET', 'Go'] },
  { label: 'Mobile', items: ['React Native', 'Flutter', 'iOS', 'Android'] },
  { label: 'AI / ML', items: ['PyTorch', 'TensorFlow', 'LangChain', 'OpenAI'] },
  { label: 'Cloud', items: ['AWS', 'Azure', 'GCP', 'Kubernetes'] },
  { label: 'Security', items: ['SAST', 'DAST', 'SCA', 'IaC scanning'] },
];

const DevelopmentPage = () => {
  return (
    <div className="dev-page">
      <PageNavbar />

      {/* Hero */}
      <section className="dev-hero">
        <div className="dev-hero-background"></div>
        <div className="dev-hero-stars"></div>
        <ScrollReveal className="dev-hero-content" animateOnMount variant="fadeUp" duration={1.05}>
          <span className="dev-badge">Software Development</span>
          <h1 className="dev-hero-title">
            Secure Software, Automation,<br />
            and <span className="dev-accent">AI Solutions.</span>
          </h1>
          <p className="dev-hero-description">
            Security-first engineering for custom applications and tools — designed for
            performance, scale, and trust. Not a dev shop. A security-focused engineering
            partner.
          </p>
        </ScrollReveal>
      </section>

      {/* We Build Software the Secure Way */}
      <section className="dev-intro-section">
        <div className="dev-container">
          <div className="dev-intro-grid">
            <motion.div className="dev-intro-text" {...getRevealProps('slideLeft')}>
              <span className="dev-block-tag">Our Approach</span>
              <h2 className="dev-section-title light" style={{ textAlign: 'left' }}>
                We Build Software the Secure Way.
              </h2>
              <p className="dev-block-body">
                Most software development companies focus solely on features and delivery speed.
                Proven InfoSec builds modern applications and tools with secure architecture and
                operational trust as foundational requirements.
              </p>
              <p className="dev-block-body">
                From custom enterprise applications and AI-powered solutions to security dashboards
                and automation tools, every product we build is designed with defense-in-depth
                principles, secure coding standards, and operational security in mind.
              </p>
            </motion.div>

            <motion.div className="dev-tech-card" {...getRevealProps('slideRight', 0.12)}>
              <span className="dev-block-tag">Tech Stack</span>
              <h3 className="dev-tech-title">Modern Tooling, Production-Ready</h3>
              <div className="dev-tech-grid">
                {techStacks.map((t, i) => (
                  <div className="dev-tech-group" key={i}>
                    <div className="dev-tech-label">{t.label}</div>
                    <div className="dev-tech-items">
                      {t.items.map((item, j) => (
                        <span className="dev-tech-pill" key={j}>{item}</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Development Services */}
      <section className="dev-services-section">
        <div className="dev-container">
          <motion.div className="dev-section-header" {...getRevealProps('fadeUp')}>
            <span className="dev-block-tag">Development Services</span>
            <h2 className="dev-section-title">Six Disciplines. One Engineering Team.</h2>
            <p className="dev-section-subtitle">
              From custom applications and AI solutions to secure SDLC, APIs, automation, and
              dashboards — every product engineered with security as a foundational requirement.
            </p>
          </motion.div>

          <motion.div
            className="dev-services-grid"
            initial="hidden"
            whileInView="visible"
            viewport={defaultViewport}
            variants={{
              ...staggerContainer,
              visible: { transition: { staggerChildren: 0.08, delayChildren: 0.06 } },
            }}
          >
            {services.map((s, i) => (
              <motion.div className="dev-service-card" key={i} variants={staggerItem}>
                <div className="dev-service-icon">{s.icon}</div>
                <h3 className="dev-service-title">{s.title}</h3>
                <p className="dev-service-desc">{s.desc}</p>
                <Link to={s.href} className="dev-service-link">
                  Learn more <FaArrowRight />
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Custom App Highlight + Development Process */}
      <section className="dev-highlight-section">
        <div className="dev-container">
          <div className="dev-highlight-grid">
            <motion.div className="dev-highlight-text" {...getRevealProps('slideLeft')}>
              <span className="dev-block-tag">Custom Application Development</span>
              <h2 className="dev-section-title light" style={{ textAlign: 'left' }}>
                Bespoke Applications, Built Secure by Design.
              </h2>
              <p className="dev-block-body">
                Custom web, mobile, and enterprise applications engineered with security-first
                architecture, modern technology stacks, and scalable design patterns — from
                customer-facing portals to internal business automation.
              </p>
              <ul className="dev-bullet-list">
                <li>Enterprise web applications (React, Next.js, Node.js)</li>
                <li>Mobile applications (React Native, Flutter, native iOS/Android)</li>
                <li>Internal tools and business process automation</li>
                <li>Customer-facing portals and self-service platforms</li>
                <li>Data visualization and reporting applications</li>
                <li>Integration platforms and middleware</li>
              </ul>
            </motion.div>

            <motion.div className="dev-process-card" {...getRevealProps('slideRight', 0.12)}>
              <span className="dev-block-tag">Development Process</span>
              <h3 className="dev-process-title">A Repeatable Six-Stage Workflow</h3>
              <div className="dev-process-steps">
                {process.map((p, i) => (
                  <div className="dev-process-step" key={i}>
                    <div className="dev-process-num">{p.step}</div>
                    <div className="dev-process-content">
                      <div className="dev-process-name">{p.name}</div>
                      <div className="dev-process-desc">{p.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Secondary Capability Cards: AI + Secure SDLC + Automation */}
      <section className="dev-capability-section">
        <div className="dev-container">
          <motion.div className="dev-section-header" {...getRevealProps('fadeUp')}>
            <span className="dev-block-tag">Featured Capabilities</span>
            <h2 className="dev-section-title">Deep Dives Into What We Build.</h2>
          </motion.div>

          <div className="dev-capability-grid">
            <motion.div className="dev-capability-card" {...getRevealProps('fadeUp')}>
              <div className="dev-capability-icon"><FaBrain /></div>
              <h3 className="dev-capability-title">AI Development Solutions</h3>
              <p className="dev-capability-body">
                Intelligent solutions built with trust — engineered with secure architecture,
                responsible AI practices, and enterprise-grade reliability.
              </p>
              <div className="dev-capability-list">
                <div className="dev-capability-item"><FaCheckCircle className="dev-check-icon" /><span>Machine learning model development and deployment</span></div>
                <div className="dev-capability-item"><FaCheckCircle className="dev-check-icon" /><span>Natural language processing (NLP) solutions</span></div>
                <div className="dev-capability-item"><FaCheckCircle className="dev-check-icon" /><span>Predictive analytics and anomaly detection</span></div>
                <div className="dev-capability-item"><FaCheckCircle className="dev-check-icon" /><span>AI-powered security tools and threat detection</span></div>
                <div className="dev-capability-item"><FaCheckCircle className="dev-check-icon" /><span>Computer vision and data analysis platforms</span></div>
              </div>
            </motion.div>

            <motion.div className="dev-capability-card" {...getRevealProps('fadeUp', 0.1)}>
              <div className="dev-capability-icon"><FaShieldAlt /></div>
              <h3 className="dev-capability-title">Secure SDLC Services</h3>
              <p className="dev-capability-body">
                Build security into your software from design through deployment — automated
                security testing, developer enablement, and CI/CD security gates.
              </p>
              <div className="dev-capability-list">
                <div className="dev-capability-item"><FaCheckCircle className="dev-check-icon" /><span>Threat modeling and security requirements</span></div>
                <div className="dev-capability-item"><FaCheckCircle className="dev-check-icon" /><span>Secure coding standards and guidelines</span></div>
                <div className="dev-capability-item"><FaCheckCircle className="dev-check-icon" /><span>SAST, DAST, and SCA tool integration</span></div>
                <div className="dev-capability-item"><FaCheckCircle className="dev-check-icon" /><span>Security code review processes</span></div>
                <div className="dev-capability-item"><FaCheckCircle className="dev-check-icon" /><span>Developer security training and workshops</span></div>
              </div>
            </motion.div>

            <motion.div className="dev-capability-card" {...getRevealProps('fadeUp', 0.2)}>
              <div className="dev-capability-icon"><FaCogs /></div>
              <h3 className="dev-capability-title">Automation Tools</h3>
              <p className="dev-capability-body">
                Automate operations with precision and security — custom tools that reduce manual
                effort, improve consistency, and eliminate human error.
              </p>
              <div className="dev-capability-list">
                <div className="dev-capability-item"><FaCheckCircle className="dev-check-icon" /><span>Security orchestration and automated response (SOAR)</span></div>
                <div className="dev-capability-item"><FaCheckCircle className="dev-check-icon" /><span>IT workflow automation and orchestration</span></div>
                <div className="dev-capability-item"><FaCheckCircle className="dev-check-icon" /><span>Compliance reporting automation</span></div>
                <div className="dev-capability-item"><FaCheckCircle className="dev-check-icon" /><span>Data collection and aggregation tools</span></div>
                <div className="dev-capability-item"><FaCheckCircle className="dev-check-icon" /><span>Notification and alerting systems</span></div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="dev-cta-section">
        <div className="dev-container">
          <motion.div className="dev-cta-card" {...getRevealProps('scaleUp')}>
            <div className="dev-cta-left">
              <h2 className="dev-cta-title">Have an idea? Let's engineer it securely.</h2>
              <p className="dev-cta-desc">
                Whether it's a custom application, an AI solution, or a security dashboard —
                our engineering team is ready to design, build, and harden it. Talk to us about
                your project requirements.
              </p>
              <div className="dev-cta-actions">
                <button className="dev-cta-btn primary">Book Consultation <FaArrowRight /></button>
                <button className="dev-cta-btn secondary">View All Services</button>
              </div>
            </div>
            <div className="dev-cta-right">
              <div className="dev-cta-stat">
                <div className="dev-cta-stat-value"><FaReact /></div>
                <div className="dev-cta-stat-label">React · Next.js · TypeScript</div>
              </div>
              <div className="dev-cta-stat">
                <div className="dev-cta-stat-value"><FaMobileAlt /></div>
                <div className="dev-cta-stat-label">React Native · Flutter</div>
              </div>
              <div className="dev-cta-stat">
                <div className="dev-cta-stat-value"><FaBrain /></div>
                <div className="dev-cta-stat-label">PyTorch · TensorFlow · LangChain</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default DevelopmentPage;
